import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const campaignPrizesSlice = createSlice({
    name: "campaignPrizes",
    initialState: {
        page: 0,
        pageCount: 0,
        success: false,
        fail: false,
        listData: null,
        byVipLevelData: null,
        detailData: null,
        responseData: null,
        errorDetailData: null,
    },
    reducers: {
        doGetCampaignPrizesListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload?.pagination?.page;
            state.pageCount = action.payload?.pagination?.totalPages;
        },
        doGetCampaignPrizesListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
        },
        doGetCampaignPrizesByVipLevelSuccess: (state, action) => {
            state.byVipLevelData = action.payload.data;
        },
        doGetCampaignPrizesByVipLevelFail: (state, action) => {
            state.byVipLevelData = null;
        },
        doGetCampaignPrizesDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetCampaignPrizesDetailFail: (state, action) => {
            state.detailData = null;
        },
        doAddCampaignPrizesSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
            state.responseData = action.payload;
        },
        doAddCampaignPrizesFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doUpdateCampaignPrizesSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateCampaignPrizesFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doToggleCampaignPrizeStatusSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doToggleCampaignPrizeStatusFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doDeleteCampaignPrizesSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doDeleteCampaignPrizesFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        resetCampaignPrizesStateSuccess: (state, action) => {
            state.success = action.payload;
            state.fail = action.payload;
            state.detailData = null;
            state.errorDetailData = null;
        },
    },
});

/**
 * Get campaign prizes list with pagination and filters
 */
export const doGetCampaignPrizesList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/campaign-prizes?page=${param.page}&size=${param.limit}`;

        if (param.campaign_type) {
            url += `&campaign_type=${encodeURIComponent(param.campaign_type)}`;
        }
        if (param.vip_level) {
            url += `&vip_level=${encodeURIComponent(param.vip_level)}`;
        }
        if (param.is_active !== undefined) {
            url += `&is_active=${param.is_active}`;
        }

        const response = await axios.get(url);
        dispatch(doGetCampaignPrizesListSuccess(response.data));
    } catch (error) {
        dispatch(doGetCampaignPrizesListFail(error.response));
    }
};

/**
 * Get prizes grouped by VIP level
 */
export const doGetCampaignPrizesByVipLevel = (param = {}) => async (dispatch) => {
    try {
        let url = `/api/admin/campaign-prizes/by-vip-level`;

        if (param.campaign_type) {
            url += `?campaign_type=${encodeURIComponent(param.campaign_type)}`;
        }

        const response = await axios.get(url);
        dispatch(doGetCampaignPrizesByVipLevelSuccess(response.data));
    } catch (error) {
        dispatch(doGetCampaignPrizesByVipLevelFail(error.response));
    }
};

/**
 * Get single campaign prize details
 */
export const doGetCampaignPrizesDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prizes/${param.id}`;
        const response = await axios.get(url);
        dispatch(doGetCampaignPrizesDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetCampaignPrizesDetailFail(error.response));
    }
};

/**
 * Add new campaign prize
 */
export const doAddCampaignPrizes = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prizes`;
        const response = await axios.post(url, param);
        dispatch(doAddCampaignPrizesSuccess(response.data));
    } catch (error) {
        dispatch(doAddCampaignPrizesFail(error.response));
    }
};

/**
 * Update campaign prize
 */
export const doUpdateCampaignPrizes = (param, id) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prizes/${id}`;
        const response = await axios.put(url, param);
        dispatch(doUpdateCampaignPrizesSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateCampaignPrizesFail(error.response));
    }
};

/**
 * Toggle prize active status
 */
export const doToggleCampaignPrizeStatus = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prizes/${param.id}/toggle-status`;
        const response = await axios.patch(url);
        dispatch(doToggleCampaignPrizeStatusSuccess(response.data));
    } catch (error) {
        dispatch(doToggleCampaignPrizeStatusFail(error.response));
    }
};

/**
 * Delete campaign prize (soft delete)
 */
export const doDeleteCampaignPrizes = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/campaign-prizes/${param.id}`;
        const response = await axios.delete(url);
        dispatch(doDeleteCampaignPrizesSuccess(response.data));
    } catch (error) {
        dispatch(doDeleteCampaignPrizesFail(error.response));
    }
};

/**
 * Reset campaign prizes state
 */
export const resetCampaignPrizesState = () => async (dispatch) => {
    dispatch(resetCampaignPrizesStateSuccess(false));
};

// Export actions
export const {
    doGetCampaignPrizesListSuccess,
    doGetCampaignPrizesListFail,
    doGetCampaignPrizesByVipLevelSuccess,
    doGetCampaignPrizesByVipLevelFail,
    doGetCampaignPrizesDetailSuccess,
    doGetCampaignPrizesDetailFail,
    doAddCampaignPrizesSuccess,
    doAddCampaignPrizesFail,
    doUpdateCampaignPrizesSuccess,
    doUpdateCampaignPrizesFail,
    doToggleCampaignPrizeStatusSuccess,
    doToggleCampaignPrizeStatusFail,
    doDeleteCampaignPrizesSuccess,
    doDeleteCampaignPrizesFail,
    resetCampaignPrizesStateSuccess,
} = campaignPrizesSlice.actions;

// Export selectors
export const getCampaignPrizesList = (state) => state.campaignPrizes?.listData;
export const getCampaignPrizesListPage = (state) => state.campaignPrizes?.page;
export const getCampaignPrizesListPageCount = (state) => state.campaignPrizes?.pageCount;
export const getCampaignPrizesByVipLevel = (state) => state.campaignPrizes?.byVipLevelData;
export const getCampaignPrizesDetail = (state) => state.campaignPrizes?.detailData;
export const getCampaignPrizesErrorDetailData = (state) => state.campaignPrizes?.errorDetailData;
export const getCampaignPrizesSuccess = (state) => state.campaignPrizes?.success;
export const getCampaignPrizesFail = (state) => state.campaignPrizes?.fail;
export const getCampaignPrizesResponseData = (state) => state.campaignPrizes?.responseData;

export default campaignPrizesSlice.reducer;
